var moment = require('moment');

// Compact distance between now and a due timestamp for the Scheduled status
// chip: "3h" under a day, "4d" in calendar days, "2mo" from 60 days out.
// Direction is implied by the chip's status label (Overdue and Neglected =
// past, Due Soon = future), so only the magnitude is shown.
export function formatDueDelta(dueMoment, now) {
  if (!dueMoment || !dueMoment.isValid || !dueMoment.isValid()) return null;
  var nowMoment = now || moment();

  var absMinutes = Math.abs(dueMoment.diff(nowMoment, 'minutes'));
  var hours = Math.round(absMinutes / 60);
  if (hours < 24) {
    return (hours < 1 ? 1 : hours) + 'h';
  }

  var dayDiff = Math.abs(
    dueMoment
      .clone()
      .startOf('day')
      .diff(nowMoment.clone().startOf('day'), 'days'),
  );
  if (dayDiff < 1) dayDiff = 1;
  if (dayDiff >= 60) return Math.round(dayDiff / 30.44) + 'mo';
  return dayDiff + 'd';
}

// IANA zone used when a day-granularity row's rule zone is missing or not
// recognized by this runtime's Intl data. UTC is a fixed, viewer-independent
// stand-in -- it is never mistaken for a real answer the way silently
// falling back to the viewer's own zone would be (that is the B8 bug).
export var FALLBACK_TIME_ZONE = 'UTC';

// Y/M/D (and, with `withTime`, h/mm/am-pm) of `momentOrDate` as it falls on the
// calendar in `timeZone`, via Intl (this codebase has moment but not the
// moment-timezone data package). An invalid or unrecognized zone falls back to FALLBACK_TIME_ZONE rather
// than throwing or resolving against whatever zone the runtime happens to be
// in.
function zonedDateParts(momentOrDate, timeZone, withTime) {
  var date = moment(momentOrDate).toDate();
  var options = {
    timeZone: timeZone || FALLBACK_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };
  if (withTime) {
    options.hour = 'numeric';
    options.minute = '2-digit';
    options.hour12 = true;
  }
  var formatter;
  try {
    formatter = new Intl.DateTimeFormat('en-US', options);
  } catch (e) {
    formatter = new Intl.DateTimeFormat(
      'en-US',
      Object.assign({}, options, { timeZone: FALLBACK_TIME_ZONE }),
    );
  }
  var parts = formatter.formatToParts(date);
  var result = {};
  for (var i = 0; i < parts.length; i++) {
    result[parts[i].type] = parts[i].value;
  }
  return result;
}

// MM/DD/YYYY for `momentOrDate` as it falls on the calendar in `timeZone` --
// the row's own rule zone, not the viewer's. The Scheduled tab is one merged
// list drawn from rules in many zones, so formatting in the viewer's zone can
// show the wrong calendar day (B8). Day-coarse due dates are stored at noon
// in the rule's zone (API convention), which buys ~11h of safety even when
// read naively, but that is not a substitute for this: two zones far enough
// apart (e.g. Pacific/Auckland and America/Los_Angeles, 19h apart) still
// disagree on the date even from noon.
export function formatZonedDate(momentOrDate, timeZone) {
  var parts = zonedDateParts(momentOrDate, timeZone);
  return parts.month + '/' + parts.day + '/' + parts.year;
}

// MM/DD/YYYY h:mm am for `momentOrDate` in `timeZone` -- a timed rule's due
// instant shown in the rule's own zone (S19, ruling Q26), not the viewer's.
// Lower-case am/pm matches the table's moment 'h:mm a' cells.
export function formatZonedDateTime(momentOrDate, timeZone) {
  var parts = zonedDateParts(momentOrDate, timeZone, true);
  var dayPeriod = parts.dayPeriod ? parts.dayPeriod.toLowerCase() : '';
  return (
    parts.month + '/' + parts.day + '/' + parts.year + ' ' +
    parts.hour + ':' + parts.minute + ' ' + dayPeriod
  );
}

// True when `dueMoment` falls on the same calendar date as `now`, both read
// in `timeZone` (the rule's own zone). Meaningful only for day-granularity
// rows: a day-coarse rule due today is classified `due-soon` by the API, not
// `overdue`, precisely so it can still be scheduled for the day -- this just
// tells the UI when to say "Due Today" instead of a bare date and delta.
export function isDueToday(dueMoment, timeZone, now) {
  if (!dueMoment || !dueMoment.isValid || !dueMoment.isValid()) return false;
  var nowMoment = now || moment();
  var due = zonedDateParts(dueMoment, timeZone);
  var today = zonedDateParts(nowMoment, timeZone);
  return (
    due.year === today.year &&
    due.month === today.month &&
    due.day === today.day
  );
}
